import './EditCalendar.css'
import { ErrorAlert } from '../../common/ErrorAlert';
import { useState } from "react";
import { Button, Form, Input, Spin } from "antd";
import { mapToCalendar } from '../../../models/calendar/Event.model';
import { useConfig } from '../../../contexts/ConfigContext';
import { Calendar } from '../../../models/calendar/Calendar.model';
import { useModalCalendar } from '../../../contexts/calendar/ModalCalendarContext.context';
import { useCompany } from '../../../contexts/CompanyContext';
import { CalendarService } from '../../../services/calendar.api.client';
import { useCalendars } from '../../../contexts/calendar/CalendarsContext.context';
import { useUpdater } from '../../../contexts/calendar/UpdateCalendarSelect.context';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { Space, InputNumber, Divider } from 'antd';


type Props = {
    calendar:Calendar
}

type FieldType = {
    name: string;
    description: string;
    uidCalendar: string;
    uidCompany: string;
    customEvents?: { id?: string; name: string; duration: number }[];
};

export const EditCalendar = ({ calendar }: Props)=>{
    const [currentCalendar,setCurrentCalendar] = useState<Calendar>(calendar);
    const {company} = useCompany();
    const {setCalendars} = useCalendars();
    const [errorText,setErrorText] = useState<string>('');
    const [statusForm,setStatusForm] = useState<string>('SUCCESS');
    const {setOptions} = useModalCalendar();
    const [form] = Form.useForm<FieldType>();
    const {config} = useConfig();
    const {setUpdater} = useUpdater();
    
    const cerrarModal = () =>{
        setOptions((prev)=>({
            ...prev,
            isopen:false,
            id:'',
            isCreating:false,
            isConfiguration:false,
            width: "520px"
        }))
    }

    const handleSubmit = (dataForm:FieldType) => {
        if(config.key == null || config.key == ''){
            setErrorText('No se ha podido obtener la clave de seguridad, intente nuevamente.');
            return;
        }
        setStatusForm('LOAD');
        // Merge only the editable fields onto the existing calendar object
        // so that uid, id, users, etc. are preserved for the backend
        const customEvents = dataForm.customEvents?.map(ev => ({
            ...ev,
            id: ev.id || Math.random().toString(36).substring(7)
        })) || [];
        
        const calendarObj: Partial<Calendar> = {
            ...currentCalendar,
            name: dataForm.name,
            description: dataForm.description,
            customEvents,
            company_id: company.current.id,
        };
        CalendarService.updateCalendar(calendarObj, company.current, config.key).then((response)=>{
            const data: Calendar = response.calendar || response;
            setCurrentCalendar(data);
            setCalendars((prevCalendars:Calendar[])=>{
                const index = prevCalendars.findIndex((calendar:Calendar)=> calendar.id == data.id || calendar.uid == data.uid);
                if(index !== -1){
                    const updatedEvents = [...prevCalendars];
                    updatedEvents[index] = data;
                    return updatedEvents;
                }
                return prevCalendars;
            });
            setStatusForm('SUCCESS');
            setUpdater((prev)=>!prev);
        }).catch((reason)=>{
            setStatusForm('SUCCESS');
            setErrorText(reason.message);
        })
    };

    switch(statusForm){
        case 'LOAD':
            return(
                <div className="w-[100%] container-load">
                     <Spin size="large" />
                </div>
            );

        default:
            return (
                <>
                <div style={{padding:'0 20px'}}>
                    <ErrorAlert message={errorText} onDismiss={() => setErrorText('')} />
                </div>
                <Form className="edit-form pr-5 pl-5 pb-2" onFinish={handleSubmit} form={form} layout="vertical"
                initialValues={{
                    uidCalendar: currentCalendar.id,
                    uidCompany: company.current.uid,
                    name: currentCalendar.name,
                    description: currentCalendar.description,
                    customEvents: currentCalendar.customEvents || [],
                }}>

                    <Form.Item label={null} hidden={true} name="uidCalendar">
                            <input type="hidden" name="uidCalendar"/>
                    </Form.Item>
                    <Form.Item label={null} hidden={true} name="uidCompany">
                        <input type="hidden" name="uidCompany"/>
                    </Form.Item>
        
                    <Form.Item<FieldType>
                        label="Nombre"
                        name="name"
                        rules={[{ required: true, message: 'Ingrese un nombre para el calendario!' }]}
                        className="space-y-2 mb-2 w-[100%]"
                    >
                        <Input type="text" name="name"/>
                    </Form.Item>
                    <Form.Item<FieldType>
                        label="Descripción"
                        name="description"
                        rules={[{ required: true, message: 'Ingrese una descripción para el calendario!' }]}
                        className="space-y-2 mb-2 w-[100%]"
                    >
                        <Input type="text" name="description"/>
                    </Form.Item>
                    
                    <Divider style={{ margin: '16px 0' }} orientation="left">Eventos Personalizados</Divider>
                    <Form.List name="customEvents">
                        {(fields, { add, remove }) => (
                            <>
                                {fields.map(({ key, name, ...restField }) => (
                                    <div key={key} style={{ display: 'flex', marginBottom: 8, gap: '8px', alignItems: 'baseline', width: '100%' }}>
                                        <Form.Item
                                            {...restField}
                                            name={[name, 'id']}
                                            hidden
                                        >
                                            <Input />
                                        </Form.Item>
                                        <Form.Item
                                            {...restField}
                                            name={[name, 'name']}
                                            rules={[{ required: true, message: 'Falta nombre' }]}
                                            style={{ flex: 1, marginBottom: 0 }}
                                        >
                                            <Input placeholder="Nombre del evento (ej. Consulta)" />
                                        </Form.Item>
                                        <Form.Item
                                            {...restField}
                                            name={[name, 'duration']}
                                            rules={[{ required: true, message: 'Falta duración' }]}
                                            style={{ width: '140px', marginBottom: 0 }}
                                        >
                                            <InputNumber placeholder="Duración (min)" suffix="(min)" min={1} style={{ width: '100%' }} />
                                        </Form.Item>
                                        <MinusCircleOutlined onClick={() => remove(name)} style={{ marginTop: '10px' }} />
                                    </div>
                                ))}
                                <Form.Item>
                                    <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                                        Añadir evento personalizado
                                    </Button>
                                </Form.Item>
                            </>
                        )}
                    </Form.List>
                    
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '16px', paddingTop: '16px' }}>
                        <Button size="large" onClick={() => cerrarModal()}>Cerrar</Button>
                        <Button size="large" type="primary" htmlType="submit">Guardar</Button>
                    </div>
                </Form>
                </>
            )
    }
}
